import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  LinearProgress,
  Typography,
} from "components/mui";
import { AddIcon, ClearIcon, UploadIcon } from "components/icons";
import SyncIcon from "@mui/icons-material/Sync";
import MailingListEditor from "containers/pages/CANmail/components/mailingListEditor/MailingListEditor";
import MailingListTable from "containers/pages/CANmail/components/mailingListTable/MailingListTable";
import { BulkContactUploadModal } from "containers/pages/contacts/components";
import { getMailLists, deleteMailLists } from "api/canmail/CANmailAPI";
import { getCompanies } from "api";

const ListManager = () => {
  const [mailingLists, setMailingLists] = useState([]);
  const [showNewListEditor, setShowNewListEditor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isSyncOpen, setIsSyncOpen] = useState(false);

  useEffect(() => {
    loadMailingLists();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading companies:", error);
      setCompanies([]);
    }
  };

  const loadMailingLists = async () => {
    setIsLoading(true);
    try {
      const lists = await getMailLists();
      if (lists && Array.isArray(lists)) {
        setMailingLists(lists);
      } else {
        console.warn("getMailLists returned invalid data:", lists);
        setMailingLists([]);
      }
    } catch (error) {
      console.error("Error loading mailing lists:", error);
      setMailingLists([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await deleteMailLists(JSON.stringify([listId]));
      await loadMailingLists(); // Refresh the list
    } catch (error) {
      console.error("Error deleting list:", error);
      // You could add a notification here to inform the user
    }
  };

  const handleNewListCreated = () => {
    setShowNewListEditor(false);
    loadMailingLists(); // Refresh the list
  };

  const handleListUpdated = (listId, newName) => {
    setMailingLists(prevLists => 
      prevLists.map(list => 
        list.list_id === listId 
          ? { ...list, list_name: newName }
          : list
      )
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Mailing Lists
      </Typography>

      {isLoading && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={showNewListEditor ? <ClearIcon /> : <AddIcon />}
          onClick={() => setShowNewListEditor(!showNewListEditor)}
        >
          {showNewListEditor ? "Cancel" : "Create New List"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setIsBulkUploadOpen(true)}
        >
          Bulk Import
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<SyncIcon />}
          onClick={() => setIsSyncOpen(true)}
        >
          Sync Contacts
        </Button>
      </Box>

      {showNewListEditor && (
        <Box sx={{ mb: 3 }}>
          <MailingListEditor
            setMailingLists={setMailingLists}
            onClose={handleNewListCreated}
          />
        </Box>
      )}

      <MailingListTable
        mailingLists={mailingLists}
        setMailingLists={setMailingLists}
        onDelete={handleDeleteList}
        onListUpdated={handleListUpdated}
        onCloned={loadMailingLists}
      />

      <BulkContactUploadModal
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        companies={companies}
        onContactsRefresh={loadMailingLists}
      />

      <BulkContactUploadModal
        open={isSyncOpen}
        onClose={() => setIsSyncOpen(false)}
        companies={companies}
        mode="sync"
        onContactsRefresh={loadMailingLists}
      />
    </Box>
  );
};

export default ListManager;
